import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { fallProtectionInspectionToolTips } from "../../../constants/jsaConstants";
import styles from "./jsaFallProtectionInfoComponents.module.css";

// Helper function to get field error for Fall Protection fields
const getFallProtectionFieldError = (autosaveErrors, fieldName) => {
  const key = `jsa_fall_protection.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

export const InspectionItemTooltip = ({ itemName, description }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({
    top: -9999,
    left: -9999,
    placement: "below",
  });
  const tooltipRef = useRef(null);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);

  // Handle escape key, outside click, and dynamic positioning
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape" && showTooltip) {
        setShowTooltip(false);
      }
    };

    const handleGlobalClick = (e) => {
      if (
        showTooltip &&
        tooltipRef.current &&
        buttonRef.current &&
        !tooltipRef.current.contains(e.target) &&
        !buttonRef.current.contains(e.target)
      ) {
        setShowTooltip(false);
      }
    };

    const updatePosition = () => {
      if (!buttonRef.current || !tooltipRef.current) return;

      const btnRect = buttonRef.current.getBoundingClientRect();
      const ttEl = tooltipRef.current;

      const ttWidth = ttEl.offsetWidth;
      const ttHeight = ttEl.offsetHeight;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let placement = "below";
      let top = btnRect.bottom + 10;

      // If no room below, place above
      if (top + ttHeight > viewportHeight - 8) {
        placement = "above";
        top = btnRect.top - ttHeight - 10;
      }

      // Horizontal clamping within viewport
      let left = btnRect.left;
      if (left + ttWidth > viewportWidth - 8) {
        left = Math.max(8, viewportWidth - ttWidth - 8);
      }
      left = Math.max(8, left);

      setTooltipPos({ top, left, placement });
    };

    if (showTooltip) {
      document.addEventListener("keydown", handleEscapeKey);
      document.addEventListener("click", handleGlobalClick);
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      requestAnimationFrame(updatePosition);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.removeEventListener("click", handleGlobalClick);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [showTooltip]);

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTooltip(!showTooltip);
  };

  return (
    <div className={styles.itemButtonContainer} ref={containerRef}>
      <button
        ref={buttonRef}
        className={styles.inspectionItemButton}
        onClick={handleButtonClick}
        aria-label={`${itemName} inspection details`}
        type="button"
      >
        {itemName}
      </button>

      {showTooltip &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className={`${styles.tooltip} ${styles.tooltipFixed} ${
              tooltipPos.placement === "above"
                ? styles.tooltipAbove
                : styles.tooltipBelow
            }`}
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
          >
            <button
              className={styles.tooltipCloseButton}
              onClick={() => setShowTooltip(false)}
              aria-label="Close tooltip"
            >
              ×
            </button>
            {description}
          </div>,
          document.body,
        )}
    </div>
  );
};

// JSA V1 Fall Protection Yes No Section
export const JsaFallProtectionYesNoSection = ({ callbacks }) => {
  // console.log("callbacks", callbacks);
  // Handling the fall protection yes checkbox
  const handleFallProtectionYes = (e) => {
    if (!e.target.checked) {
      // Putting in a confirm since the data will be deleted
      const confirm = window.confirm(
        "By unchecking this, all fall protection equipment information will be deleted. Click OK to continue or Cancel to keep the data.",
      );
      if (confirm) {
        callbacks.v1Actions.updateJsaFallProtectionYes(
          callbacks.jsaData,
          e.target.checked,
          callbacks.emitPatch,
        );
      }
    } else {
      callbacks.v1Actions.updateJsaFallProtectionYes(
        callbacks.jsaData,
        e.target.checked,
        callbacks.emitPatch,
      );
    }
  };

  // Handling the fall protection no checkbox
  const handleFallProtectionNo = (e) => {
    if (
      e.target.checked &&
      callbacks.jsaV1.fallProtection.showFallProtectionSection
    ) {
      const confirm = window.confirm(
        "By checking this, all fall protection equipment information will be deleted. Click OK to continue or Cancel to keep the data.",
      );
      if (confirm) {
        callbacks.v1Actions.updateJsaFallProtectionNo(
          callbacks.jsaData,
          e.target.checked,
          callbacks.emitPatch,
        );
      }
    } else {
      callbacks.v1Actions.updateJsaFallProtectionNo(
        callbacks.jsaData,
        e.target.checked,
        callbacks.emitPatch,
      );
    }
  };

  return (
    <div className={styles.jsaFpInfoSection}>
      <div className={styles.jsaFpRow}>
        <div className={styles.jsaFpRowText}>
          <p>Will fall protection equipment be required?</p>
        </div>
      </div>
      <div className={styles.jsaFpRow}>
        <div className={styles.jsaFpRowInputContainer}>
          <div className={styles.jsaFpRowInput}>
            <label className={styles.jsaFpRowLabel}>Yes</label>
            <input
              type="checkbox"
              checked={callbacks.jsaData.jsaFallProtection.fpEquipReqYes}
              onChange={handleFallProtectionYes}
              className={styles.jsaFpRowInputCheckbox}
              disabled={callbacks.jsaMode === "view"}
            />
          </div>
          <div className={styles.jsaFpRowInput}>
            <label className={styles.jsaFpRowLabel}>No</label>
            <input
              type="checkbox"
              checked={callbacks.jsaData.jsaFallProtection.fpEquipReqNo}
              onChange={handleFallProtectionNo}
              className={styles.jsaFpRowInputCheckbox}
              disabled={callbacks.jsaMode === "view"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// JSA V1 Fall Protection Equipment Checkbox Row
export const JsaFallProtectionEquipmentCheckboxRow = ({
  equipmentType,
  valueState,
  checkboxFunc,
  jsaData,
  mode = "edit",
  emitPatch,
}) => {
  // Handling the checkbox change
  const handleCheckboxChange = (e) => {
    checkboxFunc(jsaData, e.target.checked, emitPatch);
  };

  return (
    <div className={styles.jsaFpEquipmentRow}>
      <label className={styles.jsaFpRowLabel}>{equipmentType}</label>
      <input
        type="checkbox"
        checked={valueState}
        onChange={handleCheckboxChange}
        className={styles.jsaFpRowInputCheckbox}
        disabled={mode === "view"}
      />
    </div>
  );
};

// JSA V1 Fall Protection Equipment Section
export const JsaFallProtectionEquipmentSection = ({ callbacks }) => {
  // Get autosave error for other equipment info
  const otherInfoError = getFallProtectionFieldError(
    callbacks.autosaveErrors,
    "fp_equip_other_info",
  );

  // Handle updating the Other fall protection text (local state only on onChange)
  const handleOtherFallProtectionText = (e) => {
    callbacks.v1Actions.updateJsaFallProtectionOtherInfo(
      callbacks.jsaData,
      e.target.value,
      null, // Don't emit on every keystroke
    );
  };

  // Handle blur - send patch to server
  const handleOtherFallProtectionBlur = (e) => {
    callbacks.v1Actions.updateJsaFallProtectionOtherInfo(
      callbacks.jsaData,
      e.target.value,
      callbacks.emitPatch, // Emit on blur
    );
  };

  return (
    <div className={styles.jsaFpInfoSection}>
      <div className={`${styles.jsaFpEquipmentGrid}`}>
        <JsaFallProtectionEquipmentCheckboxRow
          equipmentType="Body Harness"
          valueState={callbacks.jsaData.jsaFallProtection.fpEquipBodyHarness}
          checkboxFunc={callbacks.v1Actions.updateJsaFallProtectionBodyHarness}
          jsaData={callbacks.jsaData}
          mode={callbacks.jsaMode}
          emitPatch={callbacks.emitPatch}
        />
        <JsaFallProtectionEquipmentCheckboxRow
          equipmentType="Shock Lanyard"
          valueState={callbacks.jsaData.jsaFallProtection.fpEquipShockLanyard}
          checkboxFunc={callbacks.v1Actions.updateJsaFallProtectionShockLanyard}
          jsaData={callbacks.jsaData}
          mode={callbacks.jsaMode}
          emitPatch={callbacks.emitPatch}
        />
        <JsaFallProtectionEquipmentCheckboxRow
          equipmentType="Retractable Lanyard"
          valueState={callbacks.jsaData.jsaFallProtection.fpEquipRetracLanyard}
          checkboxFunc={
            callbacks.v1Actions.updateJsaFallProtectionRetracLanyard
          }
          jsaData={callbacks.jsaData}
          mode={callbacks.jsaMode}
          emitPatch={callbacks.emitPatch}
        />
        <div className={styles.jsaFpEquipmentRow}>
          <label className={styles.jsaFpRowLabel}>Other</label>
          <input
            type="text"
            value={callbacks.jsaData.jsaFallProtection.fpEquipOtherInfo}
            onChange={handleOtherFallProtectionText}
            onBlur={handleOtherFallProtectionBlur}
            className={`${styles.jsaFpRowInputText} ${otherInfoError ? styles.inputError : ""}`}
            disabled={callbacks.jsaMode === "view"}
          />
          {otherInfoError && (
            <span className={styles.errorText}>{otherInfoError}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to get field error for Fall Protection Inspection List Item fields
const getFallProtectionInspectionItemFieldError = (
  autosaveErrors,
  rowId,
  fieldName,
) => {
  // Check for row-specific error first
  const rowKey = `jsa_fall_protection_inspection.${rowId}.${fieldName}`;
  if (autosaveErrors?.fieldErrors?.[rowKey]) {
    return autosaveErrors.fieldErrors[rowKey];
  }
  // Fallback to generic inspection field error
  const genericKey = `jsa_fall_protection_inspection.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[genericKey] || null;
};

// JSA V1 Fall Protection Inspection List Item Row
export const JsaFallProtectionInspectionListItemCard = ({
  inspection,
  callbacks,
}) => {
  // Get error for serial number field
  const serialNumberError = getFallProtectionInspectionItemFieldError(
    callbacks.autosaveErrors,
    inspection.rowId,
    "equipment_serial_number",
  );

  // Handling removing a, inspection list item
  const handleRemoveInspectionListItem = () => {
    callbacks.v1Actions.removeJsaFallProtectionInspectionListItem(
      callbacks.jsaData,
      inspection,
      callbacks.emitPatch,
    );
  };

  // Function wrappers to handle the updating up checkboxes and text

  // Handle inspection serial number update (local state only on onChange)
  const handleInspectionSerialNumberUpdate = (e) => {
    callbacks.v1Actions.updateJsaFallProtectionInspectionListItemEquipmentSn(
      callbacks.jsaData,
      inspection,
      e.target.value,
      null, // Don't emit on every keystroke
    );
  };

  // Handle blur - send patch to server
  const handleInspectionSerialNumberBlur = (e) => {
    callbacks.v1Actions.updateJsaFallProtectionInspectionListItemEquipmentSn(
      callbacks.jsaData,
      inspection,
      e.target.value,
      callbacks.emitPatch, // Emit on blur
    );
  };

  // Generic handler for all inspection accept/reject updates using switch statement
  const handleInspectionAcceptRejectUpdate = (type, value) => {
    callbacks.v1Actions.updateJsaFallProtectionInspectionListItemAcceptReject(
      callbacks.jsaData,
      inspection,
      type,
      value,
      callbacks.emitPatch,
    );
  };

  // Main switch function to handle different inspection types
  const getInspectionHandler = (inspectionType) => {
    return (e) => {
      const value = e.target.checked;

      switch (inspectionType) {
        case "HardwareInspectionAccept":
          handleInspectionAcceptRejectUpdate("HardwareInspectionAccept", value);
          break;
        case "HardwareInspectionReject":
          handleInspectionAcceptRejectUpdate("HardwareInspectionReject", value);
          break;
        case "WebbingInspectionAccept":
          handleInspectionAcceptRejectUpdate("WebbingInspectionAccept", value);
          break;
        case "WebbingInspectionReject":
          handleInspectionAcceptRejectUpdate("WebbingInspectionReject", value);
          break;
        case "StitchingInspectionAccept":
          handleInspectionAcceptRejectUpdate(
            "StitchingInspectionAccept",
            value,
          );
          break;
        case "StitchingInspectionReject":
          handleInspectionAcceptRejectUpdate(
            "StitchingInspectionReject",
            value,
          );
          break;
        case "SyntheticRopeInspectionAccept":
          handleInspectionAcceptRejectUpdate(
            "SyntheticRopeInspectionAccept",
            value,
          );
          break;
        case "SyntheticRopeInspectionReject":
          handleInspectionAcceptRejectUpdate(
            "SyntheticRopeInspectionReject",
            value,
          );
          break;
        case "WireRopeInspectionAccept":
          handleInspectionAcceptRejectUpdate("WireRopeInspectionAccept", value);
          break;
        case "WireRopeInspectionReject":
          handleInspectionAcceptRejectUpdate("WireRopeInspectionReject", value);
          break;
        case "EnergyAbsorbInspectionAccept":
          handleInspectionAcceptRejectUpdate(
            "EnergyAbsorbInspectionAccept",
            value,
          );
          break;
        case "EnergyAbsorbInspectionReject":
          handleInspectionAcceptRejectUpdate(
            "EnergyAbsorbInspectionReject",
            value,
          );
          break;
        case "LabelsInspectionAccept":
          handleInspectionAcceptRejectUpdate("LabelsInspectionAccept", value);
          break;
        case "LabelsInspectionReject":
          handleInspectionAcceptRejectUpdate("LabelsInspectionReject", value);
          break;
        default:
          console.warn(`Unknown inspection type: ${inspectionType}`);
          break;
      }
    };
  };

  return (
    <div className={styles.jsaFpInspectionListItemCard}>
      <div className={styles.jsaFpInspectionListItemHeader}>
        <div className={styles.jsaFpInspectionListItemEquipSn}>
          <label className={styles.jsaFpInspectionListItemRowLabel}>
            Equipment Serial Number:
          </label>
        </div>
        <div className={styles.jsaFpInspectionHeaderRow}>
          <input
            type="text"
            value={inspection.equipmentSerialNumber}
            onChange={handleInspectionSerialNumberUpdate}
            onBlur={handleInspectionSerialNumberBlur}
            className={`${styles.jsaFpInspectionListItemEquipSnInput} ${serialNumberError ? styles.inputError : ""}`}
            disabled={callbacks.jsaMode === "view"}
          />
          <div className={styles.jsaFpInspectionListDeleteButtonWrapper}>
            <button
              onClick={handleRemoveInspectionListItem}
              className={styles.jsaFpInspectionListDeleteButton}
              disabled={callbacks.jsaMode === "view"}
            >
              Delete
            </button>
          </div>
        </div>
        {serialNumberError && (
          <span className={styles.errorText}>{serialNumberError}</span>
        )}
      </div>

      <div className={styles.jsaFpInspectionListRows}>
        <div className={styles.jsaFpInspectionListRow}>
          <div className={styles.jsaFpInspectionItem}>
            <InspectionItemTooltip
              itemName={
                fallProtectionInspectionToolTips.hardwareInspection.label
              }
              description={
                fallProtectionInspectionToolTips.hardwareInspection.description
              }
            />
          </div>
          <div className={styles.jsaFpInspectionAcceptReject}>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Accept
              </label>
              <input
                type="checkbox"
                checked={inspection.hardwareInspectionAccept}
                onChange={getInspectionHandler("HardwareInspectionAccept")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Reject
              </label>
              <input
                type="checkbox"
                checked={inspection.hardwareInspectionReject}
                onChange={getInspectionHandler("HardwareInspectionReject")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
          </div>
        </div>

        <div className={styles.jsaFpInspectionListRow}>
          <div className={styles.jsaFpInspectionItem}>
            <InspectionItemTooltip
              itemName={
                fallProtectionInspectionToolTips.webbingInspection.label
              }
              description={
                fallProtectionInspectionToolTips.webbingInspection.description
              }
            />
          </div>
          <div className={styles.jsaFpInspectionAcceptReject}>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Accept
              </label>
              <input
                type="checkbox"
                checked={inspection.webbingInspectionAccept}
                onChange={getInspectionHandler("WebbingInspectionAccept")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Reject
              </label>
              <input
                type="checkbox"
                checked={inspection.webbingInspectionReject}
                onChange={getInspectionHandler("WebbingInspectionReject")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
          </div>
        </div>
        <div className={styles.jsaFpInspectionListRow}>
          <div className={styles.jsaFpInspectionItem}>
            <InspectionItemTooltip
              itemName={
                fallProtectionInspectionToolTips.stitchingInspection.label
              }
              description={
                fallProtectionInspectionToolTips.stitchingInspection.description
              }
            />
          </div>
          <div className={styles.jsaFpInspectionAcceptReject}>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Accept
              </label>
              <input
                type="checkbox"
                checked={inspection.stitchingInspectionAccept}
                onChange={getInspectionHandler("StitchingInspectionAccept")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Reject
              </label>
              <input
                type="checkbox"
                checked={inspection.stitchingInspectionReject}
                onChange={getInspectionHandler("StitchingInspectionReject")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
          </div>
        </div>

        <div className={styles.jsaFpInspectionListRow}>
          <div className={styles.jsaFpInspectionItem}>
            <InspectionItemTooltip
              itemName={
                fallProtectionInspectionToolTips.syntheticRopeInspection.label
              }
              description={
                fallProtectionInspectionToolTips.syntheticRopeInspection
                  .description
              }
            />
          </div>
          <div className={styles.jsaFpInspectionAcceptReject}>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Accept
              </label>
              <input
                type="checkbox"
                checked={inspection.syntheticRopeInspectionAccept}
                onChange={getInspectionHandler("SyntheticRopeInspectionAccept")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Reject
              </label>
              <input
                type="checkbox"
                checked={inspection.syntheticRopeInspectionReject}
                onChange={getInspectionHandler("SyntheticRopeInspectionReject")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
          </div>
        </div>

        <div className={styles.jsaFpInspectionListRow}>
          <div className={styles.jsaFpInspectionItem}>
            <InspectionItemTooltip
              itemName={
                fallProtectionInspectionToolTips.wireRopeInspection.label
              }
              description={
                fallProtectionInspectionToolTips.wireRopeInspection.description
              }
            />
          </div>
          <div className={styles.jsaFpInspectionAcceptReject}>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Accept
              </label>
              <input
                type="checkbox"
                checked={inspection.wireRopeInspectionAccept}
                onChange={getInspectionHandler("WireRopeInspectionAccept")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Reject
              </label>
              <input
                type="checkbox"
                checked={inspection.wireRopeInspectionReject}
                onChange={getInspectionHandler("WireRopeInspectionReject")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
          </div>
        </div>

        <div className={styles.jsaFpInspectionListRow}>
          <div className={styles.jsaFpInspectionItem}>
            <InspectionItemTooltip
              itemName={
                fallProtectionInspectionToolTips.energyAbsorbInspection.label
              }
              description={
                fallProtectionInspectionToolTips.energyAbsorbInspection
                  .description
              }
            />
          </div>
          <div className={styles.jsaFpInspectionAcceptReject}>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Accept
              </label>
              <input
                type="checkbox"
                checked={inspection.energyAbsorbInspectionAccept}
                onChange={getInspectionHandler("EnergyAbsorbInspectionAccept")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Reject
              </label>
              <input
                type="checkbox"
                checked={inspection.energyAbsorbInspectionReject}
                onChange={getInspectionHandler("EnergyAbsorbInspectionReject")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
          </div>
        </div>

        <div className={styles.jsaFpInspectionListRow}>
          <div className={styles.jsaFpInspectionItem}>
            <InspectionItemTooltip
              itemName={fallProtectionInspectionToolTips.labelsInspection.label}
              description={
                fallProtectionInspectionToolTips.labelsInspection.description
              }
            />
          </div>
          <div className={styles.jsaFpInspectionAcceptReject}>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Accept
              </label>
              <input
                type="checkbox"
                checked={inspection.labelsInspectionAccept}
                onChange={getInspectionHandler("LabelsInspectionAccept")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
            <div>
              <label className={styles.jsaFpInspectionCheckboxLabel}>
                Reject
              </label>
              <input
                type="checkbox"
                checked={inspection.labelsInspectionReject}
                onChange={getInspectionHandler("LabelsInspectionReject")}
                className={styles.jsaFpInspectionCheckbox}
                disabled={callbacks.jsaMode === "view"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// JSA V1 Fall Protection Inspection List Section
export const JsaFallProtectionInspectionListSection = ({ callbacks }) => {
  // Handling the add inspection list item button
  const handleAddInspectionListItem = () => {
    callbacks.v1Actions.addJsaFallProtectionInspectionListItem(
      callbacks.jsaData,
      callbacks.emitPatch,
    );
  };

  return (
    <div className={styles.jsaFpInfoSection}>
      <div className={styles.jsaFpInspectionCardGrid}>
        {callbacks.jsaData.jsaFallProtection.fallProtectionInspectionList.map(
          (inspection) => (
            <JsaFallProtectionInspectionListItemCard
              key={inspection.rowId || Math.random().toString()}
              callbacks={callbacks}
              inspection={inspection}
            />
          ),
        )}
      </div>

      <div className={styles.jsaFpInspectionListAddButtonWrapper}>
        <button
          onClick={handleAddInspectionListItem}
          className={styles.jsaFpInspectionListAddButton}
          disabled={callbacks.jsaMode === "view"}
        >
          Add Fall Protection Equipment
        </button>
      </div>
    </div>
  );
};

// JSA V1 Fall Protection Info Tab
export const JsaFallProtectionInfoTab = ({ callbacks }) => {
  return (
    <div className={styles.jsaFallProtectionInfoTab}>
      <JsaFallProtectionYesNoSection callbacks={callbacks} />
      {callbacks.jsaData.jsaFallProtection.fpEquipReqYes && (
        <>
          <JsaFallProtectionEquipmentSection callbacks={callbacks} />
          <JsaFallProtectionInspectionListSection callbacks={callbacks} />
        </>
      )}
    </div>
  );
};
